<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { motion } from "motion-v";
import { useAuth } from "@/composables/useAuth";
import { useSocket } from "@/composables/useSocket";
import apiClient from "@/composables/common/axios";

const { isLoggedIn, currentUser } = useAuth();
const { on, off } = useSocket();
const bettingHistory = ref([]);

// API로 배팅 이력 조회
const fetchBettingHistory = async () => {
  try {
    if (!isLoggedIn.value) {
      bettingHistory.value = [];
      return;
    }

    const response = await apiClient.get('/api/user.php?action=history&limit=20');

    if (response.data.success) {
      bettingHistory.value = response.data.history;
    }
  } catch (error) {
    console.error('[Betting History Table] Fetch failed:', error);
    bettingHistory.value = [];
  }
};

// 배팅 즉시 "대기중" 상태로 추가
const addPendingBet = (data) => {
  if (!isLoggedIn.value) return;

  const now = new Date();
  const pendingBet = {
    roundId: data.round_id || 'pending',
    date: now.toISOString().split('T')[0],
    time: now.toTimeString().split(' ')[0].substring(0, 5),
    type: data.bet_type,
    originalAmount: data.amount,
    approvedAmount: data.amount,
    profit: 0,
    result: '대기중',
    status: 'pending',
    startPrice: data.start_price || 0,
    endPrice: '-',
    roundResult: '',
    _isPending: true, // 임시 배팅 플래그
    _betId: data.bet_id // 배팅 ID 저장
  };

  // 배열 맨 앞에 추가
  bettingHistory.value.unshift(pendingBet);
};

// 배팅 결과 업데이트 (해당 행만 업데이트, 전체 새로고침 X)
const updateBetResult = (data) => {
  // bet_id로 정확히 매칭 (2중 배팅 지원)
  const index = bettingHistory.value.findIndex(
    bet => bet._betId === data.bet_id && bet._isPending
  );

  if (index !== -1) {
    // 해당 행의 결과만 업데이트
    const bet = bettingHistory.value[index];
    bet.result = data.result || '정산완료';
    bet.profit = data.profit || 0;
    bet.startPrice = data.entryPrice || bet.startPrice;
    bet.endPrice = data.exitPrice || 0;
    bet.roundResult = data.result === '승리' ? 'WIN' : 'LOSE';
    bet.status = 'settled';
    bet._isPending = false; // pending 플래그 제거
  }
};

onMounted(() => {
  // 약간의 딜레이 후 배팅 이력 로드
  setTimeout(() => {
    fetchBettingHistory();
  }, 100);

  // Socket.io 이벤트 - 배팅 즉시 추가 (본인 배팅만)
  on('bet:placed', (data) => {
    // 타입 비교 문제 방지: 둘 다 숫자로 변환
    const betUserId = parseInt(data.user_id);
    const currentUserId = parseInt(currentUser.value?.id);

    if (betUserId === currentUserId) {
      addPendingBet(data);
    }
  });

  // Socket.io 이벤트 - 결과 업데이트 시 배팅 이력 새로고침
  on('results:update', () => {
    fetchBettingHistory();
  });

  // Socket.io 이벤트 - 배팅 정산 완료 시 새로고침
  on('bet:settled', (data) => {
    updateBetResult(data);
  });

  // Socket.io 이벤트 - 배팅 즉시 승인 시 새로고침 (승인이 바로 난 경우)
  on('bet:approved', () => {
    fetchBettingHistory();
  });
});

onUnmounted(() => {
  off('bet:placed');
  off('results:update');
  off('bet:settled');
  off('bet:approved');
});

// 로그인 상태 변경 시 새로고침
watch(isLoggedIn, (newVal) => {
  if (newVal) {
    fetchBettingHistory();
  } else {
    bettingHistory.value = [];
  }
});

// 숫자 포맷팅
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';

  // 이미 문자열이고 콤마가 포함된 경우 그대로 반환
  if (typeof num === 'string' && num.includes(',')) {
    return num;
  }

  // 문자열인 경우 숫자로 변환
  const numValue = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : num;

  // NaN 체크
  if (isNaN(numValue)) return '0';

  // 숫자를 한국 형식으로 포맷팅
  return numValue.toLocaleString('ko-KR');
};
</script>
<template>
  <motion.section
    class="betting-history"
    :initial="{ opacity: 0, y: 20 }"
    :animate="{ opacity: 1, y: 0 }"
    :transition="{
      type: 'spring',
      stiffness: 300,
      damping: 25,
      mass: 0.8,
    }"
  >
    <h2 class="blind">배팅이력</h2>
    <div class="table__inner">
      <div class="table__wrap">
        <table class="table table--a">
          <colgroup>
            <col width="260px" />
            <col width="140px" />
            <col width="260px" />
            <col width="260px" />
            <col width="260px" />
            <col width="260px" />
            <col width="140px" />
          </colgroup>
          <thead class="table__head">
            <tr class="table__row table__row--th">
              <th class="table__cell table__cell--th">날짜</th>
              <th class="table__cell table__cell--th">유형</th>
              <th class="table__cell table__cell--th">진입가격</th>
              <th class="table__cell table__cell--th">결산가격</th>
              <th class="table__cell table__cell--th">배팅</th>
              <th class="table__cell table__cell--th">수익</th>
              <th class="table__cell table__cell--th">결과</th>
            </tr>
          </thead>
          <tbody class="table__body">
            <!-- 로그인 안됐을 때 -->
            <tr v-if="!isLoggedIn" class="table__row table__row--td">
              <td class="table__cell table__cell--td" colspan="7">
                <div class="table__empty">
                  <i class="bi bi-lock"></i>
                  <span>로그인 후 배팅이력을 확인할 수 있습니다.</span>
                </div>
              </td>
            </tr>

            <!-- 로그인했지만 데이터 없을 때 -->
            <tr v-else-if="bettingHistory.length === 0" class="table__row table__row--td">
              <td class="table__cell table__cell--td" colspan="7">
                <div class="table__empty">
                  <span>배팅 내역이 없습니다.</span>
                </div>
              </td>
            </tr>

            <!-- 배팅 이력 표시 -->
            <tr v-else v-for="(item, index) in bettingHistory" :key="item._betId || (item.roundId + '-' + item.type + '-' + index)" class="table__row table__row--td">
              <td class="table__cell table__cell--td">
                <div class="table__cell-date">
                  <span>{{ item.date }}</span>
                  <span>{{ item.time }}</span>
                </div>
              </td>
              <td class="table__cell table__cell--td">
                <span
                  :class="[
                    'badge',
                    item.type === 'UP' ? 'badge--up' : 'badge--down',
                    'ico-after',
                    item.type === 'UP' ? 'ico-up' : 'ico-down'
                  ]"
                >
                  {{ item.type }}
                </span>
              </td>
              <td class="table__cell table__cell--td">{{ formatNumber(item.startPrice) }}</td>
              <td class="table__cell table__cell--td">{{ item.endPrice ? formatNumber(item.endPrice) : '-' }}</td>
              <td class="table__cell table__cell--td">
                {{ formatNumber(item.originalAmount) }}
              </td>
              <td
                :class="[
                  'table__cell',
                  'table__cell--td',
                  item.result === '승리' ? 'table__cell--primary' : item.result === '패배' ? 'table__cell--secondary' : ''
                ]"
              >
                <div class="table__cell-profit">
                  <span v-if="item.profit > 0">+</span>
                  <span>{{ formatNumber(item.profit) }}</span>
                </div>
              </td>
              <td
                :class="[
                  'table__cell',
                  'table__cell--td',
                  item.result === '승리' ? 'table__cell--primary' : item.result === '패배' ? 'table__cell--secondary' : ''
                ]"
              >
                {{ item.result }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </motion.section>
</template>
<style scoped></style>
